import axios, { CancelTokenSource } from "axios";
import moment from "moment";
import router from "@/router";
import { DataSourceResult } from "@/models/DataSourceResult";
import { Result } from "@/models/Result";
import { GridColumn, GridColumnExportSetting } from "@/models/GridColumn";
import { ClientAsset } from "@/models/assets/ClientAsset";
import { ProjectProperty } from "@/models/assets/ProjectProperty";
import { ProjectType } from "@/models/assets/ProjectType";
import { SitePage } from "@/models/assets/SitePage";
import { Campaign, CampaignEditViewModel, CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignEditAssetsData } from "@/models/campaigns/CampaignAsset";
import { CampaignEditCostsData } from "@/models/campaigns/CampaignEditCostsData";
import { CampaignEditCRMData } from "@/models/campaigns/CampaignEditCRMData";
import { CampaignEditFacebookData } from "@/models/campaigns/CampaignEditFacebookData";
import { CampaignEditFinishData } from "@/models/campaigns/CampaignEditFinishData";
import { CampaignEditGoalsData } from "@/models/campaigns/CampaignEditGoalsData";
import { CampaignEditGoogleData } from "@/models/campaigns/CampaignEditGoogleData";
import { CampaignEditProjectsData } from "@/models/campaigns/CampaignEditProjectsData";
import { CampaignEditTagsData } from "@/models/campaigns/CampaignEditTagsData";
import { CampaignEditWhatsappPhonesData } from "@/models/campaigns/CampaignEditWhatsappPhonesData";
import { CampaignGridView } from "@/models/campaigns/CampaignGridView";
import { CampaignTag } from "@/models/campaigns/CampaignTag";
import { CampaignsStatisticsByDate } from "@/models/campaigns/CampaignsStatisticsByDate";
import { CreatePhoneViewModel } from "@/models/phones/CreatePhoneViewModel";
import { requestConfig } from "./ApiService";
import ConfigService from "../ConfigService";

export default {
  changeDefaultCampaignAsset(channelId: number, projectId: number, projectTypeId: number, projectPageId?: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.changeDefaultCampaignAssetUrl);
      const config = {
        ...requestConfig,
        params: {
          channelId,
          projectId,
          projectPageId,
          projectTypeId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  checkPhones(phones: string): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignCheckPhonesUrl);
      const config = {
        ...requestConfig,
        params: {
          phones
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success,
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  deleteCampaign(id: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.deleteCampaignUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.msg,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  exportCampaignsToExcel(campaigns: DataSourceResult, columns: GridColumn[]): Promise<string> {
    const columnsToExport: GridColumnExportSetting[] = columns.map((i: GridColumn) => {
      return {
        Field: i.name,
        Forbidden: i.forbidden,
        Width: i.width ? i.width.toString() : "",
        Hidden: !i.visible,
      };
    });
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.exportCampaignsToExcelUrl);
      axios
        .post(url.toString(), { Columns: columnsToExport, Campaigns: JSON.parse(JSON.stringify(campaigns)).Data }, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignAdGroups(campaignId: number, avgPrice: number, dateFrom?: string, dateTo?: string): Promise<DataSourceResult> {
    const url = new URL(ConfigService.campaignAdGroups);
    url.searchParams.append("avgPrice", avgPrice.toString());
    url.searchParams.append("channelID", campaignId.toString());
    url.searchParams.append("sort", "AdGroupId-desc");
    if (dateFrom) {
      url.searchParams.append("dateFrom", dateFrom);
    }
    if (dateTo) {
      url.searchParams.append("dateTo", dateTo);
    }
    return new Promise((resolve, reject) => {
      axios
        .get(url.toString(), requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignAds(campaignId: number, avgPrice: number, groupId: string, dateFrom?: string, dateTo?: string): Promise<DataSourceResult> {
    const url = new URL(ConfigService.campaignAds);
    url.searchParams.append("avgPrice", avgPrice.toString());
    url.searchParams.append("channelID", campaignId.toString());
    url.searchParams.append("groupId", groupId);
    url.searchParams.append("sort", "AdGroupId-desc");
    if (dateFrom) {
      url.searchParams.append("dateFrom", dateFrom);
    }
    if (dateTo) {
      url.searchParams.append("dateTo", dateTo);
    }
    return new Promise((resolve, reject) => {
      axios
        .post(url.toString(), requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignAssets(payload: CampaignsPayload): Promise<DataSourceResult> {
    return this.getDataWithCampaignsPayload(payload, ConfigService.campaignAssetsUrl);
  },

  getCampaignEditAppData(campaign: Campaign): Promise<CampaignEditCRMData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignAppDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditAssetsData(id: number): Promise<CampaignEditAssetsData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignAssetsDataUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditCostsData(campaign: Campaign): Promise<CampaignEditCostsData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignCostsDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditCRMData(campaign: Campaign): Promise<CampaignEditCRMData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignCRMDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditFacebookData(campaign: Campaign): Promise<CampaignEditFacebookData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignFacebookDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditFinishData(campaign: Campaign): Promise<CampaignEditFinishData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignFinishDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditGoalsData(isNewChannel: boolean): Promise<CampaignEditGoalsData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignGoalsDataUrl);
      const config = {
        ...requestConfig,
        params: {
          isNewChannel
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditGoogleData(campaign: Campaign): Promise<CampaignEditGoogleData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignGoogleDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditPhonesData(campaignId: number): Promise<CreatePhoneViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignPhonesDataUrl);
      const config = {
        ...requestConfig,
        params: {
          channelId: campaignId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditProjectsData(campaign: Campaign): Promise<CampaignEditProjectsData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignProjectsDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditTagsData(campaign: Campaign): Promise<CampaignEditTagsData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignTagsDataUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditViewModel(id: number, createPhone: boolean): Promise<CampaignEditViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignUrl);
      const config = {
        ...requestConfig,
        params: {
          id,
          createPhone
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            switch (result.Message) {
              case "404":
                router.push({ name: "NotFound"});
                break;
              default:
                reject(result.Message);
                break;
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignEditWhatsappPhonesData(campaignId: number): Promise<CampaignEditWhatsappPhonesData> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignWhatsAppPhonesDataUrl);
      const config = {
        ...requestConfig,
        params: {
          campaignId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignKeywords(campaignId: number, groupId: string, adId: string, dateFrom?: string, dateTo?: string): Promise<DataSourceResult> {
    const url = new URL(ConfigService.campaignKeywords);
    url.searchParams.append("channelID", campaignId.toString());
    url.searchParams.append("groupId", groupId);
    url.searchParams.append("adId", adId);
    url.searchParams.append("sort", "AdGroupId-desc");
    if (dateFrom) {
      url.searchParams.append("dateFrom", dateFrom);
    }
    if (dateTo) {
      url.searchParams.append("dateTo", dateTo);
    }
    return new Promise((resolve, reject) => {
      axios
        .post(url.toString(), requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaigns(payload: CampaignsPayload, cancelTokenSource?: CancelTokenSource): Promise<DataSourceResult> {
    return this.getDataWithCampaignsPayload(payload, ConfigService.campaignsUrl, cancelTokenSource);
  },

  getCampaignsPageData(): Promise<CampaignsPageViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignsPageDataUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignsStatsByDate(payload: CampaignsPayload, cancelTokenSource?: CancelTokenSource): Promise<CampaignsStatisticsByDate[]> {
    return this.getDataWithCampaignsPayload(payload, ConfigService.campaignsStatisticByDateUrl, cancelTokenSource);
  },

  getCampaignsWithStats(payload: CampaignsPayload, cancelTokenSource?: CancelTokenSource): Promise<CampaignGridView[]> {
    return this.getDataWithCampaignsPayload(payload, ConfigService.campaignsWithStatsUrl, cancelTokenSource);
  },

  getClientAssets(clientId: number, projectTypeId: ProjectType, campaignId: number, projectId?: number): Promise<ClientAsset[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignProjectListUrl);
      const config = {
        ...requestConfig,
        params: {
          clientId,
          projectTypeId,
          channelId: campaignId,
          projectId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getClientAutoCompleteProperties(clientId: number, projectTypeId: ProjectType, campaignId: number, projectId?: number): Promise<ProjectProperty[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.clientAutoCompletePropertiesUrl);
      const config = {
        ...requestConfig,
        params: {
          clientId,
          projectTypeId,
          channelId: campaignId,
          projectId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getDataWithCampaignsPayload(payload: CampaignsPayload, urlString: string, cancelTokenSource?: CancelTokenSource): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = new URL(urlString);
      if (payload.campaignsIds) {
        for (let i = 0; i < payload.campaignsIds.length; i++) {
          url.searchParams.append(`campaignsIds[${i}]`, payload.campaignsIds[i].toString());
        }
      }
      if (payload.channelId) {
        url.searchParams.append("channelId", payload.channelId.toString());
      }
      if (payload.projectId) {
        url.searchParams.append("projectId", payload.projectId.toString());
      }
      if (payload.clientId) {
        url.searchParams.append("сlientId", payload.clientId.toString());
      }
      if (payload.dateFrom) {
        url.searchParams.append("DateFrom", payload.dateFrom.toString());
      }
      if (payload.datePeriod) {
        url.searchParams.append("DatePeriod", payload.datePeriod);
      }
      if (payload.dateTo) {
        url.searchParams.append("DateTo", payload.dateTo.toString());
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.group) {
        url.searchParams.append("group", payload.group);
      }
      if (payload.isMediaChannel) {
        url.searchParams.append("isMediaChannel", payload.isMediaChannel.toString());
      }
      if (payload.page) {
        url.searchParams.append("page", payload.page.toString());
      }
      if (payload.pageSize) {
        url.searchParams.append("pageSize", payload.pageSize.toString());
      }
      if (payload.projects) {
        for (let i = 0; i < payload.projects.length; i++) {
          url.searchParams.append(`projects[${i}]`, payload.projects[i].toString());
        }
      }
      if (payload.sort) {
        url.searchParams.append("sort", payload.sort);
      }
      if (payload.token) {
        url.searchParams.append("token", payload.token);
      }
      const config = {
        ...requestConfig,
        cancelToken: cancelTokenSource?.token,
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            reject(error);
          }
        });
    });
  },

  getSitePages(clientId: number, siteID: number): Promise<SitePage[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignProjectPagesListUrl);
      const config = {
        ...requestConfig,
        params: {
          clientId,
          siteID
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getTagsForCampaign(campaignId: number): Promise<DataSourceResult> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.tagsForCampaignUrl);
      const config = {
        ...requestConfig,
        params: {
          channelId: campaignId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  renewPhone(createPhoneViewModel: CreatePhoneViewModel): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignRenewPhoneUrl);
      axios
        .post(url.toString(), createPhoneViewModel, requestConfig)
        .then((response) => {
          resolve({
            Data: response.data.phone,
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignApp(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignAppUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignAssets(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignAssetsUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignCost(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignCostUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignCRM(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignCRMUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignFacebook(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignFacebookUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignGeneralInfo(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignGeneralInfoUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Data: response.data.channelid,
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignGoals(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignGoalsUrl);
      const startDateString: string | undefined = campaign.StartDate ? moment(campaign.StartDate).format("DD/MM/YYYY") : undefined;
      const endDateString: string | undefined = campaign.EndDate ? moment(campaign.EndDate).format("DD/MM/YYYY") : undefined;
      const config = {
        ...requestConfig,
        params: {
          StartDateString: startDateString,
          EndDateString: endDateString,
        }
      };
      axios
        .post(url.toString(), campaign, config)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignGoogle(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignGoogleUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignMetadataType(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignMetadataTypeUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignProjects(campaign: Campaign, projectsIds: number[]): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignProjectsUrl);
      const config = {
        ...requestConfig,
        params: {
          commitments: JSON.stringify(projectsIds),
        }
      };
      axios
        .post(url.toString(), campaign, config)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveCampaignTags(campaign: Campaign, tagsIds: number[]): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignTagsUrl);
      const config = {
        ...requestConfig,
        params: {
          tags: JSON.stringify(tagsIds),
        }
      };
      axios
        .post(url.toString(), campaign, config)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  savePhones(createPhoneViewModel: CreatePhoneViewModel): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignSavePhonesUrl);
      axios
        .post(url.toString(), createPhoneViewModel, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  saveWhatsappPhones(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.saveCampaignWhatsappPhonesUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  sendMessage(email: string, msg: string): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignSendMessageUrl);
      const config = {
        ...requestConfig,
        params: {
          email,
          msg,
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  setActive(campaign: Campaign): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignSetActiveUrl);
      axios
        .post(url.toString(), campaign, requestConfig)
        .then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  toggleActive(id: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignToggleActiveUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.msg,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  togglePhoneState(createPhoneViewModel: CreatePhoneViewModel): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.campaignTogglePhoneStateUrl);
      axios
        .post(url.toString(), createPhoneViewModel, requestConfig)
        .then((response) => {
          resolve({
            Data: response.data.phone || response.data.errorType,
            Message: response.data.message,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  updateTagForCampaign(campaignTag: CampaignTag): Promise<DataSourceResult> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.tagsForCampaignUpdateUrl);
      axios
        .post(url.toString(), campaignTag, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export interface CampaignsPayload {
  campaignsIds?: number[];
  channelId?: number;
  projectId?: number;
  clientId?: number;
  dateFrom?: string;
  datePeriod?: string;
  dateTo?: string;
  filter?: string;
  group?: string;
  isMediaChannel?: boolean;
  page?: number;
  pageSize: number;
  sort?: string;
  projects?: number[];
  token?: string;
}

export interface GetCampaignEditViewModelPayload {
  createPhone: boolean,
  id: number,
}
